import { api, getAccessToken, type DirectiveCheck, type DirectiveCandidate, type DirectivesContext } from '../api';
import { callout, close, openDialog } from '../dialog';
import { esc, fmtDate, html, pill, type State } from '../ui';
import { addDirective } from './directive';
import { openPdfViewer } from './pdf-viewer';

/**
 * What the published registers hold against this aircraft.
 *
 * The list is candidates, not applicability. The search is keyed on the
 * aircraft's own identifiers — type, ICAO designator, fitted engine and
 * propeller — and a keyword match tells you a document mentions something the
 * aircraft has, not that the document applies to it. ML.A.201 leaves that
 * judgement with the organisation, so nothing here is recorded until someone
 * reads the document and records it.
 *
 * Two things are therefore said plainly rather than politely: when a source
 * could not be reached the result is incomplete and must not be read as
 * "nothing new", and a serial number that was not found in a document means
 * nothing was found — not that the aircraft is unaffected.
 */

const SOURCE_LABEL: Record<string, string> = {
  EASA_AD: 'EASA AD',
  ROTAX_SB: 'Rotax SB',
  ROTAX_ASB: 'Rotax ASB',
  BRISTELL_SB: 'Bristell SB',
};

/**
 * How firmly the publisher put it, in their colour.
 *
 * The words are the manufacturer's own — Bristell publish exactly these — so
 * the label is shown verbatim and only the tone is ours. Anything unrecognised
 * still gets a neutral pill rather than being dropped, because a classification
 * we have not seen before is precisely the one worth reading.
 */
const CLASSIFICATION_TONE: Record<string, State> = {
  mandatory: 'OVERDUE',
  'could become ad': 'DUE_SOON',
  recommended: 'DUE_SOON',
  optional: 'OK',
  informational: 'NA',
  cancelled: 'NA',
};

export function showDirectiveCheck(
  check: DirectiveCheck,
  ctx: DirectivesContext,
  reload: () => void,
): void {
  const row = (c: DirectiveCandidate, index: number) => html`
    <div class="row" style="border-left-color:var(--${c.onFile ? 'ok' : c.serialMatch === true ? 'caution' : 'line-2'})">
      <div class="bd">
        <div class="t">
          <span style="font-family:var(--mono)">${esc(c.reference)}</span>${c.revision ? ` ${esc(c.revision)}` : ''}
          <span class="sub2">${esc(SOURCE_LABEL[c.source] ?? c.source)}</span>
        </div>
        <div class="m">${esc(c.title || '(no title published)')}</div>
        <div class="m tiny dim">
          ${c.issuedAt ? esc(fmtDate(c.issuedAt)) : 'no date'} · found on ${esc(c.matchedOn)}
          ${c.serialMatch === true
            ? ' · this aircraft’s serial number appears in the document'
            : c.serialMatch === null && c.url
              ? ' · serial numbers not checked'
              : ''}
        </div>
      </div>
      <!-- Deliberately not wrapping. The pills and the two buttons are one
           control group; letting them wrap put "Record" on a line of its own
           under "Open", which reads as a separate action. The description
           column carries min-width:0 and yields the space instead. -->
      <div style="display:flex;gap:6px;align-items:center;white-space:nowrap;flex:none">
        ${c.classification
          ? pill(CLASSIFICATION_TONE[c.classification.toLowerCase()] ?? 'NA', c.classification)
          : ''}
        ${c.serialMatch === true ? pill('DUE_SOON', 'Serial match') : ''}
        ${c.onFile ? pill('OK', 'On file') : ''}
        ${c.url ? html`<button class="btn sm" type="button" data-open-doc="${index}">Open</button>` : ''}
        ${c.onFile ? '' : html`<button class="btn sm" type="button" data-record="${index}">Record</button>`}
      </div>
    </div>
  `;

  const fresh = check.results.filter((r) => !r.onFile);
  const known = check.results.filter((r) => r.onFile);

  openDialog<null>({
    wide: true,
    title: `Published directives — ${check.registration}`,
    sub: `${check.newCount} not yet recorded · ${check.onFileCount} already on file`,
    primary: 'Close',

    body: () => html`
      ${check.complete
        ? ''
        : callout('exceed', '✕', html`
            <b>This result is incomplete.</b> One or more sources could not be reached, so it does not
            mean nothing has been published:
            <ul style="margin:6px 0 0 18px">
              ${check.sources
                .filter((s) => s.error)
                .map((s) => `<li>${esc(s.name)} — ${esc(s.searched)}: ${esc(s.error!)}</li>`)
                .join('')}
            </ul>
          `)}

      ${callout('info', 'i', html`
        Searched the published registers on <b>${esc(check.searchedOn.join(', '))}</b>. What comes back is
        a list of documents mentioning something this aircraft has — <b>not</b> a finding that they apply
        to it. Read the document and record the ones that do; that assessment is yours to make and is what
        an auditor asks to see.
        ${check.serialsChecked.length
          ? html`<br><br>Serial numbers looked for: <span style="font-family:var(--mono)">${esc(check.serialsChecked.join(', '))}</span>.
              Not finding one proves nothing — many documents state applicability in prose.`
          : ''}
      `)}

      ${fresh.length
        ? html`
            <div class="fset">
              <div class="lg">Not yet recorded — ${fresh.length}</div>
              <div class="rows" style="max-height:340px;overflow:auto">
                ${fresh.map((c) => row(c, check.results.indexOf(c))).join('')}
              </div>
            </div>
          `
        : callout('ok', '✓', html`
            Everything the registers returned is already recorded against this aircraft.
          `)}

      ${known.length
        ? html`
            <div class="fset">
              <div class="lg">Already on file — ${known.length}</div>
              <div class="rows" style="max-height:200px;overflow:auto">
                ${known.map((c) => row(c, check.results.indexOf(c))).join('')}
              </div>
            </div>
          `
        : ''}

      <details class="card" style="margin-top:12px">
        <summary class="card-h">
          <div class="sum">
            <span class="twist" aria-hidden="true"></span>
            <div>
              <h2 style="font-size:13px">What was searched</h2>
              <p class="tiny dim">
                ${check.sources.length} quer${check.sources.length === 1 ? 'y' : 'ies'} across
                ${esc(check.searchedOn.join(', '))}
              </p>
            </div>
          </div>
          ${check.sources.some((s) => s.error) ? pill('OVERDUE', 'a source failed') : ''}
        </summary>
        <div class="rows">
          ${check.sources
            .map(
              (s) => html`
                <div class="row">
                  <div class="bd">
                    <div class="t">${esc(s.name)}</div>
                    <div class="m" style="font-family:var(--mono)">${esc(s.searched)}</div>
                  </div>
                  <div class="m">${s.error ? pill('OVERDUE', 'could not reach') : `${s.found}`}</div>
                </div>
              `,
            )
            .join('')}
        </div>
      </details>
    `,

    onRender: (root) => {
      // Re-served through the API rather than linked directly: the publishers
      // send Content-Disposition: attachment and X-Frame-Options: SAMEORIGIN,
      // so a plain link downloads the file and an iframe pointed at their
      // origin shows nothing.
      root.querySelectorAll<HTMLElement>('[data-open-doc]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const found = check.results[Number(btn.dataset.openDoc)];
          if (!found?.url) return;
          openPdfViewer({
            path: `/aircraft/${check.aircraftId}/directives/published-pdf?url=${encodeURIComponent(found.url)}`,
            title: `${found.reference}${found.revision ? ` ${found.revision}` : ''}`,
            subtitle: found.title,
            sourceUrl: found.url,
            accessToken: getAccessToken,
          });
        }),
      );

      root.querySelectorAll<HTMLElement>('[data-record]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const found = check.results[Number(btn.dataset.record)];
          if (!found) return;
          // Straight into the record dialog with the published detail carried
          // over, so the only things left to decide are the ones that need
          // deciding: whether it applies, and by when.
          close();
          addDirective(ctx, reload, {
            authority: found.authority,
            kind: found.kind,
            reference: found.reference,
            revision: found.revision,
            subject: found.title,
            issuedAt: found.issuedAt,
            sourceUrl: found.url,
            matchedOn: found.matchedOn,
          });
        }),
      );
    },

    collect: () => null,
    submit: async () => undefined,
    done: () => '',
  });
}

/** Runs the check and shows the result. */
export async function runDirectiveCheck(aircraftId: string, reload: () => void): Promise<void> {
  const [check, ctx] = await Promise.all([
    api.directives.check(aircraftId),
    api.directives.context(aircraftId),
  ]);
  showDirectiveCheck(check, ctx, reload);
}
